import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';
import { Group } from 'three';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../../store/useAppStore';
import { useResponsiveValues } from '../../hooks/viewport/useResponsiveValues';
import { useSpiralAnimation } from '../../hooks/animation/useSpiralAnimation';
import { useMouseTracking } from '../../hooks/interaction/useMouseTracking';
import { useSpiralMaterials } from '../../hooks/materials/useSpiralMaterials';
import { useMaterialsAnimation } from '../../hooks/animation/useMaterialsAnimation';
import { usePositionAnimation } from '../../hooks/animation/usePositionAnimation';
import { useSound } from '../../hooks/audio/useSound';
import { GalleryItem } from './GalleryItem';
import type { Project } from '../../types';

interface SpinGalleryProps {
  projects: Project[];
  allMediaLoaded: boolean;
}

export const SpinGallery: React.FC<SpinGalleryProps> = ({
  projects,
  allMediaLoaded,
}) => {
  const { selectedIndex, setSelected, hasVisitedAbout, hasPlayedIntroVortex, setHasPlayedIntroVortex, isIndicatorScrolling } = useAppStore(
    useShallow(state => ({
      selectedIndex: state.selectedIndex,
      setSelected: state.setSelected,
      hasVisitedAbout: state.hasVisitedAbout,
      hasPlayedIntroVortex: state.hasPlayedIntroVortex,
      setHasPlayedIntroVortex: state.setHasPlayedIntroVortex,
      isIndicatorScrolling: state.isIndicatorScrolling,
    }))
  );
  const groupRef = useRef<Group>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const hoveredRef = useRef<number | null>(null);

  useEffect(() => {
    hoveredRef.current = hoveredIndex;
  }, [hoveredIndex]);

  useEffect(() => {
    if (isIndicatorScrolling) setHoveredIndex(null);
  }, [isIndicatorScrolling]);

  useEffect(() => {
    if (selectedIndex === null) setHoveredIndex(null);
  }, [selectedIndex]);
  const { viewport, gl } = useThree();
  const values = useResponsiveValues(viewport);

  const clearHover = useCallback(() => setHoveredIndex(null), []);

  const handleScrollInteraction = useCallback(
    (clientX: number, clientY: number, isTouch?: boolean) => {
      setHoveredIndex(null);
      if (isTouch) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          gl.domElement.dispatchEvent(
            new PointerEvent('pointermove', {
              clientX,
              clientY,
              bubbles: true,
            })
          );
        });
      });
    },
    [gl]
  );

  const {
    scrollRef,
    initAnimation,
    setInitAnimation,
    isInitialAnimationComplete,
    velocity,
  } = useSpiralAnimation(groupRef, values, handleScrollInteraction, clearHover);

  const mousePos = useMouseTracking(selectedIndex ?? -1);
  const materials = useSpiralMaterials(projects, values, allMediaLoaded);
  const { playIntroVortex, playProjectOpenWind } = useSound();

  // Play vortex sound once per session (never when returning from About)
  useEffect(() => {
    if (allMediaLoaded && !hasPlayedIntroVortex && !hasVisitedAbout) {
      setHasPlayedIntroVortex(true);
      playIntroVortex();
    }
  }, [allMediaLoaded, playIntroVortex, hasVisitedAbout, hasPlayedIntroVortex, setHasPlayedIntroVortex]);

  useMaterialsAnimation({
    materials,
    hoveredRef,
    selectedIndex,
    mousePos,
    isInitialAnimationComplete,
    initAnimation,
    scrollRef,
    projectsPerLoop: values.projectsPerLoop,
    velocity,
  });

  usePositionAnimation({
    groupRef,
    allImagesLoaded: allMediaLoaded,
    isInitialAnimationComplete,
    setInitAnimation,
    initAnimation,
    scrollRef,
    selectedIndex,
    projects,
    values,
    onNearestProjectChange: index => {
      useAppStore.setState({ nearestProjectIndex: index });
    },
  });

  const sharedGeometry = useMemo(
    () => new THREE.PlaneGeometry(1, 1, 10, 10),
    []
  );

  useEffect(() => {
    return () => sharedGeometry.dispose();
  }, [sharedGeometry]);

  const handleSelect = useCallback(
    (index: number) => {
      const project = projects[index];
      const isOpening = selectedIndex !== index;
      if (isOpening) playProjectOpenWind();
      setSelected(
        selectedIndex === index ? null : project,
        selectedIndex === index ? null : index
      );
    },
    [projects, selectedIndex, setSelected, playProjectOpenWind]
  );

  return (
    <>
      <PerspectiveCamera makeDefault position={[0, 0, 15]} fov={60} />
      <group ref={groupRef}>
        {projects.map((project, index) => (
          <GalleryItem
            key={project.id}
            project={project}
            index={index}
            originalIndex={index}
            totalProjects={projects.length}
            material={materials[index]}
            imageScale={values.imageScale}
            sharedGeometry={sharedGeometry}
            isSelected={selectedIndex === index}
            isHovered={hoveredIndex === index}
            onSelect={() => handleSelect(index)}
            onHover={useCallback(
              (isHovered: boolean) => {
                setHoveredIndex(isHovered ? index : null);
              },
              [index]
            )}
          />
        ))}
      </group>
    </>
  );
};
